import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';
import { Icon, type IconName } from './icon';
import styles from './button.module.css';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'destructive-soft';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

// Polymorphic: `as` swaps the rendered tag (e.g. Link for a button-styled
// anchor). When `as` is set, button-only props (`type`, `disabled`,
// `aria-busy`) are dropped, and extra props (e.g. `href`) flow through.
type ButtonProps = Omit<ComponentPropsWithRef<'button'>, 'type'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Phase text shown INSIDE the button while loading ("Compressing…"). With
   *  it, the spinner + label replace the children; without it, loading keeps
   *  the classic hidden-body + centred-spinner treatment. */
  loadingLabel?: ReactNode;
  fullWidth?: boolean;
  leadingIcon?: IconName;
  trailingIcon?: IconName;
  iconOnly?: boolean;
  type?: 'button' | 'submit' | 'reset';
  as?: ElementType;
  [key: string]: unknown;
};

const ICON_SIZE: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 16, xl: 18 };
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'primary',
  secondary: 'secondary',
  ghost: 'ghost',
  destructive: 'destructive',
  'destructive-soft': 'destructiveSoft',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingLabel,
  fullWidth = false,
  leadingIcon,
  trailingIcon,
  iconOnly = false,
  disabled,
  className,
  children,
  type = 'button',
  as,
  ref,
  ...props
}: ButtonProps) {
  const labelledLoading = loading && loadingLabel != null;
  const classes = [
    styles.button,
    styles[VARIANT_CLASS[variant]],
    styles[size],
    fullWidth ? styles.fullWidth : null,
    iconOnly ? styles.iconOnly : null,
    loading ? styles.loading : null,
    labelledLoading ? styles.labelledLoading : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconSize = ICON_SIZE[size];
  const Tag = (as || 'button') as ElementType;
  const isNativeButton = Tag === 'button';

  const buttonOnlyProps = isNativeButton
    ? {
        type,
        disabled: disabled || loading,
        'aria-busy': loading || undefined,
      }
    : {};

  return (
    <Tag ref={ref} className={classes} {...buttonOnlyProps} {...props}>
      {loading && !labelledLoading ? (
        <span className={styles.spinner} aria-hidden="true">
          <Icon name="loading" size={iconSize} data-icon-spin />
        </span>
      ) : null}
      {labelledLoading ? (
        <span className={styles.body} role="status" aria-live="polite">
          <span className={styles.bodySpinner} aria-hidden="true">
            <Icon name="loading" size={iconSize} />
          </span>
          <span className={styles.label}>{loadingLabel}</span>
        </span>
      ) : (
        <span className={styles.body}>
          {leadingIcon ? <Icon name={leadingIcon} size={iconSize} aria-hidden="true" /> : null}
          {children != null && !iconOnly ? <span className={styles.label}>{children}</span> : null}
          {trailingIcon ? <Icon name={trailingIcon} size={iconSize} aria-hidden="true" /> : null}
        </span>
      )}
    </Tag>
  );
}
